import {
  addDays,
  daysInMonth,
  gregorianToHijri,
  gregorianToJalali,
  hijriToGregorian,
  jalaliToGregorian,
  jdnWeekday,
  monthName,
  startOfMonth,
  toJdn,
  weekdayName,
  type CalendarSystem,
  type CivilDate,
  type Lang,
} from "@calendar/shared";

/**
 * A Saturday inside each calendar's supported range, used as column zero.
 *
 * The reference has to be per calendar: the same year/month/day triple is a
 * different weekday in each system, and the Jalali and Hijri ranges reject
 * years the Gregorian one accepts. One shared date would mislabel the columns
 * and, worse, throw for a valid request.
 */
const SATURDAY: Record<CalendarSystem, CivilDate> = {
  jalali: { year: 1405, month: 1, day: 1 },
  gregorian: { year: 2024, month: 1, day: 6 },
  hijri: { year: 1448, month: 1, day: 5 },
};

/**
 * Days from `day` back to the nearest Saturday at or before it, 0..6.
 *
 * Uses jdnWeekday, which is the project's own 0=Sunday convention, rather than
 * jdn % 7: the two differ by one, and using the raw jdn shifts every column by
 * a day and puts the weekend on Thursday and Friday.
 */
function daysSinceSaturday(day: CivilDate, calendar: CalendarSystem): number {
  const saturday = jdnWeekday(toJdn(SATURDAY[calendar], calendar));
  return (jdnWeekday(toJdn(day, calendar)) - saturday + 7) % 7;
}

/** The day cells of one month, padded to whole weeks starting on Saturday. */
export function monthDays(anchor: CivilDate, calendar: CalendarSystem): CivilDate[] {
  const first = startOfMonth(anchor, calendar);
  const lead = daysSinceSaturday(first, calendar);
  const start = addDays(first, -lead, calendar);
  const length = daysInMonth(anchor, calendar);
  const total = Math.ceil((lead + length) / 7) * 7;
  return Array.from({ length: total }, (_, i) => addDays(start, i, calendar));
}

export function weekDayNames(calendar: CalendarSystem, lang: Lang): string[] {
  const saturday = SATURDAY[calendar];
  return Array.from({ length: 7 }, (_, i) =>
    weekdayName(addDays(saturday, i, calendar), calendar, lang),
  );
}

export function monthLabel(month: number, calendar: CalendarSystem, lang: Lang): string {
  return monthName(month, calendar, lang);
}

/**
 * The two secondary dates shown in the corners of a year cell.
 *
 * Hijri is reached through Gregorian, since that is the only public entry
 * point that takes a date. Deriving both corners from the cell's own instant,
 * rather than recomputing each month by hand, is what keeps them consistent
 * with the big numeral in the middle.
 */
/**
 * The three day-numbers for one cell.
 *
 * The active calendar's number is the large one in the middle; the view picks
 * which of the other two goes in which bottom corner, so switching the active
 * calendar swaps the corners rather than leaving them pinned.
 */
export function cornerDates(
  day: CivilDate,
  calendar: CalendarSystem,
): { jalali: number; gregorian: number; hijri: number } {
  const gregorian =
    calendar === "jalali"
      ? jalaliToGregorian(day)
      : calendar === "hijri"
        ? hijriToGregorian(day)
        : day;
  return {
    jalali: gregorianToJalali(gregorian).day,
    gregorian: gregorian.day,
    hijri: gregorianToHijri(gregorian).day,
  };
}

/**
 * The Jalali year a Gregorian month falls in.
 *
 * Occasions are published on the Jalali calendar, so a Gregorian or Hijri view
 * has to know which Jalali year to ask about. A Gregorian year straddles two:
 * January is in the previous Jalali year, December in the next.
 */
export function jalaliYearForGregorian(year: number, month: number): number {
  return gregorianToJalali({ year, month, day: 1 }).year;
}

/**
 * The Jalali year a Hijri year overlaps.
 *
 * A Hijri year runs about 354 days and is shorter than a Jalali year, so a
 * Hijri year sits inside one Jalali year for most of its length and slips into
 * the next at the end. The middle of the Hijri year is the stable pivot.
 */
export function jalaliYearForHijri(year: number, _month: number): number {
  return gregorianToJalali(hijriToGregorian({ year, month: 6, day: 1 })).year;
}

/**
 * Which season a month of the active calendar falls in: 0 spring, 1 summer,
 * 2 autumn, 3 winter.
 *
 * The seasons belong to the Iranian year, not to whatever calendar is on
 * screen: Farvardin to Khordad is spring, Tir to Shahrivar summer, and so on,
 * three months each. So a Gregorian or Hijri month is converted to Jalali first.
 *
 * The day in the middle of the month is the representative, because a foreign
 * month usually straddles two Iranian months and the middle of it is the one
 * that reads right. It is always a real day: every Gregorian, Jalali and Hijri
 * month has at least 29.
 */
export function seasonOfMonth(year: number, month: number, calendar: CalendarSystem): number {
  if (calendar === "jalali") return Math.floor((month - 1) / 3);
  let jalali: CivilDate;
  try {
    jalali =
      calendar === "gregorian"
        ? gregorianToJalali({ year, month, day: 15 })
        : gregorianToJalali(hijriToGregorian({ year, month, day: 15 }));
  } catch {
    // A Hijri year outside the converter's supported range. Fall back to the
    // month's position, which is at worst a season out on the boundary.
    return Math.floor((month - 1) / 3);
  }
  return Math.floor((jalali.month - 1) / 3);
}

/** Friday and Saturday, matching the seven columns weekDayNames() returns. */
export function weekendFlags(calendar: CalendarSystem, days: CivilDate[]): boolean[] {
  return days.map((day) => {
    const offset = daysSinceSaturday(day, calendar);
    return offset === 5 || offset === 6;
  });
}
